import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CutJobClient, type PanelRef } from "../../../api/cutJobs";
import { OpenRecord } from "../../../components/OpenRecord";
import { CUT_JOBS_KEY } from "./cutJobModel";

/** One lot's panel type waiting for a cut job: a row to tick. */
interface CandidateRow {
  key: string;
  ref: PanelRef;
  lot: number;
  part_number: string;
  part_name: string;
  quantity: number;
  due_date: string;
  material: string;
  material_name: string;
  planned: string;
}

/**
 * The open production orders whose production BOM is confirmed, one row per
 * panel type not in a cut job yet (a lot cutting Noir and raw MDF is two
 * rows): tick what Cut Rite batched, filter like any table.
 */
export function CandidateTable({ processCode, onSelect }: { processCode: string; onSelect: (refs: PanelRef[]) => void }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new CutJobClient(http), [http]);
  const candidates = useQuery({ queryKey: [...CUT_JOBS_KEY, "candidates", processCode], queryFn: () => client.candidates(processCode) });
  const rows = useMemo<CandidateRow[]>(
    () =>
      (candidates.data ?? []).flatMap((lot) =>
        lot.panels.map((panel) => ({
          key: `${String(lot.production_order_id)}:${String(panel.virtual_part_id)}`,
          ref: { production_order_id: lot.production_order_id, virtual_part_id: panel.virtual_part_id },
          lot: lot.number,
          part_number: lot.part_number ?? "",
          part_name: lot.part_name ?? "",
          quantity: Number(lot.quantity),
          due_date: lot.due_date ?? "",
          material: panel.virtual_number,
          material_name: panel.virtual_name,
          planned: panel.planned ?? "",
        })),
      ),
    [candidates.data],
  );
  const byKey = useMemo(() => new Map(rows.map((row) => [row.key, row.ref])), [rows]);
  const columns = useMemo(() => {
    const c = defineColumns<CandidateRow>();
    return [
      c.number("lot", (row) => row.lot, { header: t("cut-job.lot", "Lot"), priority: 1 }),
      c.text("part_number", (row) => row.part_number, { header: t("cut-job.product", "Product"), priority: 1 }),
      c.text("part_name", (row) => row.part_name, { header: t("column.part.name", "Name"), priority: 3 }),
      c.number("quantity", (row) => row.quantity, { header: t("cut-job.quantity", "Qty"), priority: 2 }),
      c.text("material", (row) => row.material, { header: t("cut-job.material", "Material"), filter: "select", priority: 1 }),
      c.text("planned", (row) => row.planned, { header: t("cut-job.planned", "Planned"), priority: 2 }),
      c.text("due_date", (row) => row.due_date, { header: t("cut-job.due", "Due"), priority: 2 }),
    ];
  }, [t]);
  return (
    <DataTable<CandidateRow>
      data={rows}
      columns={columns}
      loading={candidates.isLoading}
      getRowId={(row) => row.key}
      selectable
      onSelectionIdsChange={(ids) => onSelect(ids.map((id) => byKey.get(id)).filter((ref): ref is PanelRef => ref != null))}
      rowActions={(row) => <OpenRecord target={{ kind: "production-order", id: row.ref.production_order_id }} label={String(row.lot)} />}
      hidePrint
      stateKey={`production.cut-job-candidates.${processCode}`}
      emptyMessage={t("cut-job.candidates.none.v2", "No open production order is waiting for a cut job. A lot appears here once its production BOM is confirmed.")}
    />
  );
}
